import React, { useState } from 'react';
import { User, AlertCircle } from 'lucide-react';
import { formatPhoneNumber } from '../../utils/formatting';

interface Step1Props {
  formData: any;
  onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

export function Step1PartnerInfo({ formData, onChange }: Step1Props) {
  const [emailError, setEmailError] = useState<string>('');

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const email = e.target.value.trim();
    if (email && !validateEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmailError(''); // Clear error as user types
    onChange(e);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    onChange({
      ...e,
      target: {
        ...e.target,
        name: e.target.name,
        value: formatted
      }
    } as React.ChangeEvent<HTMLInputElement>);
  };
  return (
    <div className="space-y-6">
      <h3 className="text-xl font-bold text-tdb-primary mb-6 flex items-center space-x-2">
        <User className="w-5 h-5 text-tdb-accent" />
        <span>Partner Information</span>
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Partner First Name *
          </label>
          <input
            type="text"
            name="partnerFirstName"
            value={formData.partnerFirstName}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Partner Last Name *
          </label>
          <input
            type="text"
            name="partnerLastName"
            value={formData.partnerLastName}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Partner Email Address *
          </label>
          <p className="text-xs text-gray-500 mb-2">This is email you have registered with our team</p>
          <input
            type="email"
            name="partnerEmail"
            value={formData.partnerEmail}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            required
            className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
              emailError
                ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-200 focus:ring-tdb-accent focus:border-tdb-accent'
            }`}
          />
          {emailError && (
            <div className="flex items-center space-x-2 mt-2 text-red-600 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>{emailError}</span>
            </div>
          )}
        </div>
        <div>
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Partner Phone Number *
          </label>
          <p className="text-xs text-gray-500 mb-2">What is the phone number of your Tax Assistance Business?</p>
          <input
            type="tel"
            name="partnerPhone"
            value={formData.partnerPhone}
            onChange={handlePhoneChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="(555) 123-4567"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Partner Tax Company Name *
          </label>
          <p className="text-xs text-gray-500 mb-2">This is the name of the Tax Assistance Company you use</p>
          <input
            type="text"
            name="partnerTaxCompany"
            value={formData.partnerTaxCompany}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-semibold text-tdb-primary mb-2">
            Tax Assistance Website *
          </label>
          <input
            type="url"
            name="taxAssistanceWebsite"
            value={formData.taxAssistanceWebsite}
            onChange={onChange}
            required
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-tdb-accent focus:border-tdb-accent transition-all"
            placeholder="https://yourwebsite.com"
          />
        </div>

      </div>
    </div>
  );
}
